import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Paperclip, MessageSquare, History, Trash2, Download } from "lucide-react";
import api from "../lib/api";
import { STATUS, PRIORITY, formatDate, initials } from "../lib/utils";
import { useAuth } from "../context/AuthContext";

const TAG_SUGGESTIONS = ["pago","informe","mantenimiento","permiso","TI","logística","comunicación","emergencia"];

export default function TaskModal({ open, onClose, task, onSaved, areas, users, projects=[] }) {
  const { user } = useAuth();
  const isNew = !task || !task.id;
  const [form, setForm] = useState({
    title: "", description: "", area_id: "", assignee_id: "",
    start_date: "", due_date: "", priority: "media", status: "pendiente",
    progress: 0, tags: [], project_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");
  const [uploading, setUploading] = useState(false);
  const [current, setCurrent] = useState(task);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (open) {
      if (task && task.id) {
        setForm({
          title: task.title || "",
          description: task.description || "",
          area_id: task.area_id || "",
          assignee_id: task.assignee_id || "",
          start_date: task.start_date || "",
          due_date: task.due_date || "",
          priority: task.priority || "media",
          status: task.status || "pendiente",
          progress: task.progress ?? 0,
          tags: task.tags || [],
          project_id: task.project_id || "",
        });
        setCurrent(task);
      } else {
        setForm({ title:"", description:"", area_id: user.area_id || (areas?.[0]?.id ?? ""), assignee_id: "", start_date:"", due_date:"", priority:"media", status:"pendiente", progress:0, tags:[], project_id:"" });
        setCurrent(null);
      }
    }
  }, [open, task, areas, user]);

  if (!open) return null;

  const canEdit = user.role !== "lectura" && (isNew ? user.role !== "colaborador" : true);
  // Colaborador solo puede modificar estado, avance y etiquetas (además de comentarios/adjuntos)
  const canEditFull = canEdit && user.role !== "colaborador";
  // Coordinador: el selector de responsable solo lista colaboradores con permiso "Puede asignar tareas" (+ sí mismo)
  const isCoord = user.role === "coordinador";
  const assignableOptions = isCoord
    ? (users || []).filter(u => u.id === user.id || (u.role === "colaborador" && u.puede_asignar && u.active !== false))
    : (users || []).filter(u => u.active !== false);

  const save = async () => {
    if (!form.title.trim()) { toast.error("Título requerido"); return; }
    if (!form.area_id) { toast.error("Área requerida"); return; }
    setSaving(true);
    try {
      let payload;
      if (isNew) {
        payload = {
          ...form,
          assignee_id: form.assignee_id || null,
          project_id: form.project_id || null,
          start_date: form.start_date || null,
          due_date: form.due_date || null,
          progress: Number(form.progress),
        };
      } else if (canEditFull) {
        payload = {
          ...form,
          assignee_id: form.assignee_id || null,
          project_id: form.project_id || null,
          start_date: form.start_date || null,
          due_date: form.due_date || null,
          progress: Number(form.progress),
        };
      } else {
        // Colaborador: solo envía los campos permitidos
        payload = { status: form.status, progress: Number(form.progress), tags: form.tags };
      }
      if (isNew) {
        const { data } = await api.post("/tasks", payload);
        toast.success("Tarea creada");
        onSaved && onSaved(data);
      } else {
        const { data } = await api.put(`/tasks/${current.id}`, payload);
        toast.success("Tarea actualizada");
        setCurrent(data);
        onSaved && onSaved(data);
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!current?.id) return;
    if (!window.confirm("¿Eliminar esta tarea? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/tasks/${current.id}`);
      toast.success("Tarea eliminada");
      onSaved && onSaved(null);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      const { data } = await api.post(`/tasks/${current.id}/comments`, { text: comment });
      setCurrent(c => ({ ...c, comments: [...(c.comments||[]), data] }));
      setComment("");
    } catch (e) { toast.error("Error al comentar"); }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/tasks/${current.id}/attachments`, fd, { headers: {"Content-Type":"multipart/form-data"}});
      setCurrent(c => ({ ...c, attachments: [...(c.attachments||[]), data] }));
      toast.success("Archivo adjuntado");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const downloadFile = async (fid, name) => {
    const token = localStorage.getItem("crejpp_token");
    const url = `${api.defaults.baseURL}/files/${fid}?auth=${encodeURIComponent(token||"")}`;
    window.open(url, "_blank");
  };

  const addTag = (t) => {
    const v = (t||tagInput).trim().toLowerCase();
    if (!v || form.tags.includes(v)) return;
    setForm(f => ({ ...f, tags: [...f.tags, v] }));
    setTagInput("");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="task-modal"
        className="bg-white rounded-lg border border-slate-200 w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{isNew ? "Nueva tarea" : "Editar tarea"}</div>
            <h3 className="font-heading text-xl font-bold text-slate-900 mt-1">{form.title || "Sin título"}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md" data-testid="task-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-6">
          {/* Row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Título">
              <input data-testid="task-title" disabled={!canEditFull} value={form.title} onChange={e => setForm({...form, title:e.target.value})} className="input" />
            </Field>
            <Field label="Área responsable">
              <select data-testid="task-area" disabled={!canEditFull} value={form.area_id} onChange={e => setForm({...form, area_id:e.target.value})} className="input">
                <option value="">— Seleccione —</option>
                {areas?.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Descripción">
            <textarea data-testid="task-description" disabled={!canEditFull} rows={3} value={form.description} onChange={e => setForm({...form, description:e.target.value})} className="input" />
          </Field>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Responsable">
              <select data-testid="task-assignee" disabled={!canEditFull} value={form.assignee_id} onChange={e => setForm({...form, assignee_id:e.target.value})} className="input">
                <option value="">— Sin asignar —</option>
                {assignableOptions.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              {isCoord && canEditFull && assignableOptions.filter(u => u.role === "colaborador").length === 0 && (
                <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-1.5" data-testid="no-assignable-hint">
                  No tiene colaboradores asignables. Comuníquese con el Administrador.
                </p>
              )}
            </Field>
            <Field label="Prioridad">
              <select data-testid="task-priority" disabled={!canEditFull} value={form.priority} onChange={e => setForm({...form, priority:e.target.value})} className="input">
                <option value="alta">Alta</option><option value="media">Media</option><option value="baja">Baja</option>
              </select>
            </Field>
            <Field label="Fecha inicio">
              <input type="date" data-testid="task-start" disabled={!canEditFull} value={form.start_date||""} onChange={e => setForm({...form, start_date:e.target.value})} className="input" />
            </Field>
            <Field label="Fecha límite">
              <input type="date" data-testid="task-due" disabled={!canEditFull} value={form.due_date||""} onChange={e => setForm({...form, due_date:e.target.value})} className="input" />
            </Field>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Estado">
              <select data-testid="task-status" disabled={!canEdit} value={form.status} onChange={e => setForm({...form, status:e.target.value})} className="input">
                {Object.entries(STATUS).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label={`Avance (${form.progress}%)`}>
              <input type="range" min="0" max="100" step="5" disabled={!canEdit} value={form.progress} onChange={e => setForm({...form, progress:e.target.value})} className="w-full accent-crejpp" />
            </Field>
            {projects.length > 0 && (
              <Field label="Proyecto (opcional)">
                <select disabled={!canEditFull} value={form.project_id||""} onChange={e => setForm({...form, project_id: e.target.value})} className="input">
                  <option value="">— Sin proyecto —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </Field>
            )}
          </div>

          <Field label="Etiquetas">
            <div className="flex flex-wrap gap-2 items-center">
              {form.tags.map(t => (
                <span key={t} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full px-3 py-1">
                  {t}
                  {canEdit && <button className="hover:text-crejpp" onClick={() => setForm(f => ({...f, tags: f.tags.filter(x => x !== t)}))}>×</button>}
                </span>
              ))}
              {canEdit && (
                <>
                  <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())} placeholder="+ etiqueta" className="text-xs px-2 py-1 border border-slate-300 rounded-md" />
                  <div className="flex flex-wrap gap-1">
                    {TAG_SUGGESTIONS.filter(t => !form.tags.includes(t)).slice(0,5).map(t => (
                      <button key={t} onClick={() => addTag(t)} className="text-[11px] text-slate-500 hover:text-crejpp px-1">+{t}</button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Field>

          {!isNew && (
            <>
              {/* Attachments */}
              <section>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-2">
                  <Paperclip className="w-3 h-3" /> Adjuntos ({(current?.attachments||[]).length})
                </div>
                <div className="space-y-2">
                  {(current?.attachments||[]).map(a => (
                    <div key={a.id} className="flex items-center justify-between bg-slate-50 rounded-md px-3 py-2 text-sm">
                      <span className="truncate">{a.original_filename}</span>
                      <button onClick={() => downloadFile(a.id, a.original_filename)} className="text-crejpp hover:underline flex items-center gap-1">
                        <Download className="w-3.5 h-3.5" /> Descargar
                      </button>
                    </div>
                  ))}
                  {canEdit && (
                    <label className="inline-flex items-center gap-2 text-sm text-slate-700 border border-dashed border-slate-300 rounded-md px-3 py-2 cursor-pointer hover:border-crejpp">
                      <Paperclip className="w-3.5 h-3.5" /> {uploading ? "Subiendo…" : "Adjuntar archivo"}
                      <input type="file" className="hidden" onChange={uploadFile} data-testid="task-attach-file" />
                    </label>
                  )}
                </div>
              </section>

              {/* Comments */}
              <section>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-2">
                  <MessageSquare className="w-3 h-3" /> Comentarios ({(current?.comments||[]).length})
                </div>
                <div className="space-y-3 mb-3">
                  {(current?.comments||[]).map(c => (
                    <div key={c.id} className="text-sm">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span className="font-semibold text-slate-700">{c.user_name}</span>
                        <span>· {formatDate(c.timestamp)}</span>
                      </div>
                      <p className="text-slate-700 mt-0.5">{c.text}</p>
                    </div>
                  ))}
                </div>
                {user.role !== "lectura" && (
                  <div className="flex gap-2">
                    <input value={comment} onChange={e => setComment(e.target.value)} placeholder="Escribir un comentario…" className="input flex-1" data-testid="task-comment-input" />
                    <button onClick={addComment} className="btn-primary" data-testid="task-comment-submit">Publicar</button>
                  </div>
                )}
              </section>

              {/* History */}
              <section>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2 flex items-center gap-2">
                  <History className="w-3 h-3" /> Historial
                </div>
                <ul className="text-xs text-slate-600 space-y-1 max-h-40 overflow-y-auto">
                  {(current?.history||[]).slice().reverse().map((h,i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-slate-400">{formatDate(h.timestamp)}</span>
                      <span className="font-semibold">{h.user_name}</span>
                      <span>· {h.action}{h.to_status ? `: ${STATUS[h.from_status]?.label||h.from_status} → ${STATUS[h.to_status]?.label||h.to_status}` : ""}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </>
          )}
        </div>

        <div className="border-t border-slate-200 p-4 flex items-center justify-between bg-slate-50">
          <div>
            {!isNew && (user.role === "administrador" || user.role === "coordinador") && (
              <button onClick={remove} className="text-crejpp text-sm inline-flex items-center gap-1 hover:underline" data-testid="task-delete-btn">
                <Trash2 className="w-3.5 h-3.5" /> Eliminar
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {!isNew && user.role === "colaborador" && (
              <span className="text-[11px] text-slate-500 hidden sm:block">Como colaborador puede actualizar estado, avance y etiquetas.</span>
            )}
            <button onClick={onClose} className="btn-secondary" data-testid="task-cancel-btn">Cancelar</button>
            {canEdit && (
              <button disabled={saving} onClick={save} className="btn-primary disabled:opacity-60" data-testid="task-save-btn">
                {saving ? "Guardando…" : (isNew ? "Crear tarea" : "Guardar cambios")}
              </button>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .input { width:100%; padding:0.5rem 0.75rem; border:1px solid #cbd5e1; border-radius:0.375rem; font-size:0.875rem; background:white; }
        .input:focus { outline: none; border-color:#EE1C25; box-shadow: 0 0 0 3px rgba(238,28,37,0.12); }
        .input:disabled { background:#f8fafc; cursor:not-allowed; }
        .btn-primary { background:#EE1C25; color:white; padding:0.5rem 0.9rem; border-radius:0.375rem; font-size:0.875rem; font-weight:600; }
        .btn-primary:hover { background:#B91419; }
        .btn-secondary { background:white; color:#334155; padding:0.5rem 0.9rem; border-radius:0.375rem; font-size:0.875rem; font-weight:500; border:1px solid #cbd5e1; }
        .btn-secondary:hover { background:#f8fafc; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
